/**
 * AsyncSelectHotels — POST-based async select for hotel autocomplete.
 *
 * Replicates AsyncSelectSeeker (ui-seeker) behaviour exactly but uses
 * postSubmit instead of getSubmit to avoid URL buffer limits when the
 * `selected` list of hotel IDs is large.
 *
 * Only used by SelectAsyncHotels. Do NOT use it for other selectors.
 */
import React, { useState, useRef, useEffect } from "react"
import AsyncSelect from "react-select/async"
import { components } from "react-select"
import { IconSeekers, useSettings } from "ui-seeker/core"
import { postSubmit } from "ui-seeker/utils"


// ─── Internal helpers (replicated from AsyncSelectSeeker source) ──────────────

const defaultHandleFilter = (option, value) =>
    option.label.toLowerCase().includes(value.toLowerCase())

const defaultHandleTheme = (theme, palette) => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary25: palette?.primary?.main,
        primary50: palette?.secondary?.main,
        primary:   palette?.primary?.main,
    },
})

function SelectSeekerIcon({ iconConfig }) {
    if (!iconConfig) return null
    return <IconSeekers
        icon={iconConfig.icon}
        iconType={iconConfig.iconType || "fontAwesome"}
        iconWeight={iconConfig.iconWeight || "fal"}
    />
}

function SelectSeekerIconInfo({ iconConfig }) {
    if (!iconConfig) return null
    return <IconSeekers
        wrapperClassName="extraInfo"
        icon={iconConfig.icon || "info-circle"}
        iconType={iconConfig.iconType || "fontAwesome"}
        tooltipText={iconConfig.text}
    />
}

function SelectSeekerLabel({ label, selected }) {
    if (!label) return null
    return <>
        <legend className={selected ? "active" : null}>{label}</legend>
        <label  className={selected ? "active" : null}>{label}</label>
    </>
}

function LimitedChipsContainer({ children, hasValue, selectProps, ...props }) {
    const chipsLimit    = selectProps?.chipsLimit     ?? 3
    const formatTitle   = selectProps?.formatTitleChip ?? null
    const showAll       = selectProps?.showAllChips    ?? false
    const setShowAll    = selectProps?.setShowAllChips

    const [chips, otherChildren] = children

    if (!hasValue) {
        return <components.ValueContainer selectProps={selectProps} hasValue={hasValue} {...props}>
            {children}
        </components.ValueContainer>
    }

    const allChips      = Array.isArray(chips) ? chips : [chips]
    const shouldLimit   = !showAll && allChips.length > chipsLimit
    const visibleChips  = shouldLimit ? allChips.slice(0, chipsLimit) : allChips
    const hiddenChips   = shouldLimit ? allChips.slice(chipsLimit)    : []
    const overflowCount = hiddenChips.length
    const title         = formatTitle
        ? hiddenChips.map(chip => formatTitle(chip.key))
        : hiddenChips.map(chip => chip.props.children)

    const handleShowAll = () => {
        setShowAll?.(true)
        selectProps?.select?.blur()
    }

    return <components.ValueContainer selectProps={selectProps} hasValue={hasValue} {...props}>
        {visibleChips}
        {overflowCount > 0 && <div
            className="root badge"
            style={{ margin: "2px", borderRadius: "2px", padding: "3px 8px" }}
            title={title.join(", ")}
            onClick={handleShowAll}
        >+{overflowCount}</div>}
        {otherChildren}
    </components.ValueContainer>
}

const CustomOption = (props) => {
    const { data, innerRef, innerProps, isSelected, isFocused, getStyles } = props
    const customStyles = {
        ...getStyles("option", props),
        display:         "flex",
        alignItems:      "center",
        backgroundColor: isSelected ? "#2474b2" : isFocused ? "#E5E5E5" : "#FFF",
        color:           isSelected ? "#FFF"     : "#2B3643",
        cursor:          "pointer",
        ":active": { backgroundColor: "#b9c4de", color: "#2B3643" },
        ":hover":  { backgroundColor: "#E5E5E5", color: "#2B3643" },
    }
    return <div ref={innerRef} {...innerProps} style={customStyles}>
        {isSelected
            ? <IconSeekers icon="check-square" iconType="fontAwesome" style={{ marginRight: 8, color: "#9ef43b" }} />
            : <IconSeekers icon="square"       iconType="fontAwesome" style={{ marginRight: 8, color: "gray"    }} />
        }
        <span>{data.label}</span>
    </div>
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AsyncSelectHotels({
    id,
    className         = "",
    urlAjax,
    extraParams       = {},
    value             = null,
    placeholder       = "Select a option...",
    isMulti           = false,
    onChange          = null,
    filterOption      = defaultHandleFilter,
    handleTheme       = defaultHandleTheme,
    limitedChips      = false,
    numberOfChips     = 2,
    formatTitleChip   = null,
    wrapperClassName  = "",
    label,
    extraInfo         = null,
    icon              = null,
    firstSelect       = false,
    additionalHeaders = {},
    ...props
}) {
    const { palette = {} } = useSettings()

    const [isLoading,    setIsLoading]    = useState(false)
    const [options,      setOptions]      = useState([])
    const [showAllChips, setShowAllChips] = useState(false)
    const [menuIsOpen,   setMenuIsOpen]   = useState(false)

    const selectWrapperRef = useRef(null)
    const selectRef        = useRef(null)
    const isFirstRender    = useRef(true)

    // Click-outside → close menu (same as AsyncSelectSeeker)
    useEffect(() => {
        const handleClickOutside = (e) => {
            const menu = document.querySelector('[class$="-menu"]')
            if (
                selectWrapperRef.current &&
                !selectWrapperRef.current.contains(e.target) &&
                (!menu || !menu.contains(e.target))
            ) {
                setMenuIsOpen(false)
                setShowAllChips(false)
                if (selectRef.current) selectRef.current.setState({ menuIsOpen: false })
            }
        }
        document.addEventListener("mousedown", handleClickOutside, true)
        return () => document.removeEventListener("mousedown", handleClickOutside, true)
    }, [])

    // POST-based options loader (replaces getSubmit in AsyncSelectSeeker)
    const promiseOptions = (inputValue) => {
        const selected = Array.isArray(value) ? value.join(",") : value || ""
        setIsLoading(true)
        return postSubmit(urlAjax, { qs: inputValue, selected, ...extraParams }, additionalHeaders)
            .then(data => {
                setOptions(data || [])
                setIsLoading(false)
                return data || []
            })
    }

    // Initial load + reload when extraParams changes (same trigger as AsyncSelectSeeker)
    useEffect(() => {
        let isMounted = true
        if (isMounted) promiseOptions("")
        return () => { isMounted = false }
    }, [JSON.stringify(extraParams)]) // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch when value is set externally (e.g. status load) and selected hotels are not yet in options.
    // Skip first render: the initial load (above) already passes selected IDs to the backend.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        const valueArr = Array.isArray(value) ? value : (value ? [value] : [])
        if (valueArr.length === 0) return
        const allPresent = valueArr.every(v => options.some(op => op.value === v))
        if (!allPresent) promiseOptions("")
    }, [JSON.stringify(value)]) // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve selected option objects from loaded options (same logic as AsyncSelectSeeker)
    let optionSelected = isMulti
        ? [...new Set(value || [])].map(v => options.find(op => op.value === v)).filter(Boolean)
        : options.filter(op => op.value === value)
    if (!isMulti && optionSelected.length === 0) optionSelected = null
    if (firstSelect && (!optionSelected || (Array.isArray(optionSelected) && !optionSelected.length))) {
        optionSelected = isMulti ? [options[0]] : options[0] || null
    }

    const customComponents = limitedChips ? {
        ValueContainer: LimitedChipsContainer,
        Option:         CustomOption,
    } : undefined

    const handleChange = (selected) => {
        setShowAllChips(false)
        onChange?.(selected)
        if (props.onSelectionChange) props.onSelectionChange(selected)
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && menuIsOpen) {
            setMenuIsOpen(false)
            setTimeout(() => {
                if (selectRef.current) selectRef.current.setState({ menuIsOpen: false })
            }, 10)
        }
        if (props.onKeyDown) props.onKeyDown(e)
    }

    const config = {
        name:              id,
        className:         `selectSeeker ${className}`,
        isMulti,
        closeMenuOnSelect: false,
        placeholder,
        onChange:          handleChange,
        onKeyDown:         handleKeyDown,
        filterOption,
        value:             optionSelected,
        theme:             (theme) => handleTheme(theme, palette),
        loadOptions:       promiseOptions,
        onBlur: () => {
            setMenuIsOpen(false)
            promiseOptions("")
        },
        isLoading,
        defaultOptions:    options,
        components:        customComponents,
        chipsLimit:        numberOfChips,
        formatTitleChip,
        showAllChips,
        setShowAllChips,
        hideSelectedOptions: !limitedChips,
        menuIsOpen,
        onMenuOpen:  () => setMenuIsOpen(true),
        onMenuClose: () => { setMenuIsOpen(false); setShowAllChips(false) },
        menuPortalTarget: document.body,
        ref: selectRef,
        styles: {
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? "#2474b2" : "#FFF",
                color:           state.isSelected ? "#FFF"     : "#2B3643",
                cursor:          "pointer",
                "&:hover":  { backgroundColor: "#E5E5E5", color: "#2B3643" },
                "&:active": { backgroundColor: "#E5E5E5", color: "#2B3643" },
            }),
            menu: (provided) => ({
                ...provided,
                borderRadius: "4px",
                boxShadow:    "0px 8px 15px rgba(0, 0, 0, 0.1)",
            }),
        },
        ...props
    }

    const iconClassWrapper = icon ? "selectSeeker-icon" : ""

    return <fieldset
        className={`selectSeeker-wrapper ${wrapperClassName} ${iconClassWrapper}`}
        ref={selectWrapperRef}
    >
        <SelectSeekerIconInfo iconConfig={extraInfo} />
        <SelectSeekerLabel
            label={label}
            selected={isMulti ? (value || []).length > 0 : !!value}
        />
        <SelectSeekerIcon iconConfig={icon} />
        <AsyncSelect {...config} />
    </fieldset>
}
